/*
 * Copyright 2026 North Carolina State University
 */

import React from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Chip
} from '@mui/material';

const roundTo = (v) => Math.round((Number(v) || 0) * 100) / 100;
const money = (v) => `$${roundTo(v).toFixed(2)}`;

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

const personName = (person) => {
  if (!person) return '—';
  if (typeof person === 'string') return person;
  const name = `${person.first_name || ''} ${person.last_name || ''}`.trim();
  return name || person.email || '—';
};

/**
 * Read-only view of the payments an admin has recorded against one invoice,
 * for the project team. Recording and editing stay on the admin side.
 *
 * The reference column carries whichever identifier the admin entered when
 * recording — a receipt number or a transaction id — so the project can match
 * a line here against its own records.
 */
const InvoicePaymentsDialog = ({ open, onClose, invoice, invoiceNumber }) => {
  const payments = Array.isArray(invoice?.payments) ? invoice.payments : [];

  // Sort newest first so the most recent payment is the one you land on.
  const sortedPayments = [...payments].sort((a, b) => {
    const aTime = a?.date ? new Date(a.date).getTime() : 0;
    const bTime = b?.date ? new Date(b.date).getTime() : 0;
    return bTime - aTime;
  });

  const total = roundTo(invoice?.total_amount);
  // Trust the recorded payments over a stale aggregate on the invoice.
  const paid = roundTo(
    payments.reduce((sum, p) => sum + (Number(p?.amount) || 0), 0)
  );
  const balance = roundTo(total - paid);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Invoice #{invoiceNumber} — Payments Received
      </DialogTitle>
      <DialogContent dividers>
        <Stack
          direction="row"
          spacing={3}
          flexWrap="wrap"
          useFlexGap
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              Invoice total
            </Typography>
            <Typography variant="h6">{money(total)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Paid
            </Typography>
            <Typography variant="h6" color="success.main">
              {money(paid)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Balance
            </Typography>
            <Typography
              variant="h6"
              color={balance > 0 ? 'error.main' : 'success.main'}
            >
              {money(balance)}
            </Typography>
          </Box>
          {balance <= 0 && total > 0 && (
            <Chip
              label="Paid in full"
              color="success"
              size="small"
              sx={{ alignSelf: 'center' }}
            />
          )}
        </Stack>

        {sortedPayments.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No payments have been recorded against this invoice yet.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Paid by</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Reference</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedPayments.map((payment, idx) => (
                  <TableRow key={payment._id || idx}>
                    <TableCell>{formatDate(payment.date)}</TableCell>
                    <TableCell>{personName(payment.paid_by)}</TableCell>
                    <TableCell>{money(payment.amount)}</TableCell>
                    <TableCell>{payment.payment_mode || '—'}</TableCell>
                    <TableCell>
                      {payment.receipt_number || payment.transaction_id || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoicePaymentsDialog;
